///
/// EarthBrowser
///
/// Created by Matt Giger on 12/30/2023
/// Copyright (c) 2023 Geodata Labs
///

import { DataLoader } from "@/earthbrowser/utils/loaders/DataLoader"
import { celestialToWorld } from "@/earthbrowser/utils/astronomy"
import { wgs } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { cube } from "@stovokor/math/cube"
import { clamp, trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { BlendFactor, DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type SkyEphemeris } from "./Sky"
import { cSkyOrderStars, skyViewProjection } from "./SkyGeometry"

const cStarDataURL = "https://tiles.earthbrowser.com/static/data/hipparcos.raw"

// Scintillation is an atmospheric effect: full strength at the surface,
// gone once the camera is above the bulk of the air
const cTwinkleFadeAltitudeKm = 40
// Radius of the celestial sphere in km: far enough that the camera's
// position within the Earth system is negligible, inside the far plane
const cStarSphereRadiusKm = 1e8

export class Stars extends NodeBase {

	constructor(context: StovokorContext, ephemeris: SkyEphemeris) {
		super()
		this.renderOrder = cSkyOrderStars
		const geometry = new StarFieldModel(context)
		this.renderer = new StarsRenderer(context, geometry, ephemeris)
	}

	public get renderReady(): boolean {
		return this.renderer?.geometry?.available ?? false
	}
}

// Source record: [right ascension * 15, declination - pi/2, magnitude, r, g, b]
// per star (radians; the odd encodings are how the catalog was exported)
const cStarFloats = 6
// Expanded vertex: unit direction (3), magnitude (1), color (3), corner (2)
const cVertexFloats = 9
const cQuadCorners = [-1, -1, 1, -1, 1, 1, -1, 1]

/**
 * Star field as one camera-facing quad per star (see stars.glsl), expanded
 * on the CPU from the raw Hipparcos records into celestial-frame unit
 * directions
 */
class StarFieldModel implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public direction: VertexAttribute
	public magnitude: VertexAttribute
	public color: VertexAttribute
	public corner: VertexAttribute
	public vertexBuffer: VertexBuffer
	public indexBuffer?: IndexBuffer
	public bounds = new cube(-1e9, 1e9)
	public boundingSphere = new sphere(1e9)
	public available: boolean = false
	private context: StovokorContext

	constructor(context: StovokorContext) {
		this.context = context
		this.direction = new VertexAttribute(context, 3)
		this.magnitude = new VertexAttribute(context, 1)
		this.color = new VertexAttribute(context, 3)
		this.corner = new VertexAttribute(context, 2)
		this.vertexBuffer = new VertexBuffer(context, [this.direction, this.magnitude, this.color, this.corner])
		void this.load(cStarDataURL)
	}

	private async load(url: string): Promise<void> {
		const loader = new DataLoader(url)
		const data = await loader.load()
		if(!data) {
			return
		}
		const stars = new Float32Array(data)
		const count = Math.floor(stars.length / cStarFloats)
		const vertices = new Float32Array(count * 4 * cVertexFloats)
		const indices = new Uint32Array(count * 6)
		let written = 0
		for(let i = 0; i < count; i++) {
			const src = i * cStarFloats
			const ra = stars[src] / 15
			const dec = stars[src + 1] + trig.half_pi
			const mag = stars[src + 2]
			// The export contains one empty record (RA 0, dec 0, magnitude 0)
			if(ra === 0 && stars[src + 1] === -trig.half_pi && mag === 0) {
				continue
			}
			const cosDec = Math.cos(dec)
			const x = Math.sin(ra) * cosDec
			const y = Math.sin(dec)
			const z = Math.cos(ra) * cosDec
			for(let c = 0; c < 4; c++) {
				const dst = (written * 4 + c) * cVertexFloats
				vertices[dst] = x
				vertices[dst + 1] = y
				vertices[dst + 2] = z
				vertices[dst + 3] = mag
				vertices[dst + 4] = stars[src + 3]
				vertices[dst + 5] = stars[src + 4]
				vertices[dst + 6] = stars[src + 5]
				vertices[dst + 7] = cQuadCorners[c * 2]
				vertices[dst + 8] = cQuadCorners[c * 2 + 1]
			}
			const v = written * 4
			indices.set([v, v + 1, v + 2, v, v + 2, v + 3], written * 6)
			written++
		}
		this.vertexBuffer.uploadData(vertices.subarray(0, written * 4 * cVertexFloats))
		this.indexBuffer = new IndexBuffer(this.context, indices.subarray(0, written * 6))
		this.available = true
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer?.destroy()
	}
}

class StarsRenderer extends Renderer {
	private sphere = matrix.scale(cStarSphereRadiusKm)
	private modelViewProjection = new UniformDynamic<matrix>()
	private time = new UniformDynamic<number>()
	private twinkle = new UniformDynamic<number>()
	private viewport = new UniformDynamic<vec2>()
	private ephemeris: SkyEphemeris
	private startJulian?: number

	constructor(context: StovokorContext, geometry: Geometry, ephemeris: SkyEphemeris) {
		const source = new ShaderSourceDefinition("stars")
		super(context, source, geometry)
		this.ephemeris = ephemeris
		// Additive: stars add light to the Milky Way drawn under them
		this.drawState.depthTest = false
		this.drawState.depthWrite = false
		this.drawState.blend = [BlendFactor.SrcAlpha, BlendFactor.One]
	}

	protected stateUpdate(state: RenderState): void {
		// Unit directions scaled out to the celestial sphere, rotated by
		// sidereal time into the Earth-fixed scene
		this.modelViewProjection.value = this.sphere
			.multiply(celestialToWorld(this.ephemeris.gmst))
			.multiply(skyViewProjection(state))

		// Wall-clock seconds since the first frame: small enough to survive
		// the shader's float precision, unlike an absolute julian date
		this.startJulian ??= state.realTime.julian
		this.time.value = (state.realTime.julian - this.startJulian) * 86400
		this.viewport.value = new vec2(state.width, state.height)

		const altitudeKm = state.camera.position.length - wgs.a
		this.twinkle.value = 1 - clamp(altitudeKm / cTwinkleFadeAltitudeKm, 0, 1)
	}
}
